import type { PropsWithChildren } from 'react'
import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { useMoteDeleteMutation } from '@mote/api'
import { Durations, MotePlayerProviders, VolumeRich } from '@mote/presentation'
import { useMotePlayer } from '@mote/presentation/src/shared/contexts/motePlayer.context'
import { useMoteEdit } from '@mote/recipes'
import {
  ContentEditable,
  PlayerButtonRow,
  PlayerHeader,
  Scrubber,
  Transcription
} from '@mote/ui'
import { PlaybackRateButton } from '@mote/ui/components'
import { useAuth } from '../auth'
import { LandingMainLayout } from '../layouts'
import { useMoteConversationContext, useMotePlaybackLocation } from '../mote'
import { StickyButton } from './StickyButton'
import {
  ControlsWrapper,
  StyledPlayerButtonRowWrapper,
  StyledScrubberWrapper,
  StyledSticky,
  StyledTranscription
} from './WebPlayer.styles'

export interface WebPlayerProps {
  isAnonymous: boolean
}

export function WebPlayer(
  props: PropsWithChildren<WebPlayerProps>
): JSX.Element {
  const moteConversationContext = useMoteConversationContext()
  return (
    <MotePlayerProviders
      isLandingPage={true}
      moteId={moteConversationContext.getMoteStatus().moteId}
    >
      <WebPlayerWithMotePlayer isAnonymous={props.isAnonymous}>
        {props.children}
      </WebPlayerWithMotePlayer>
    </MotePlayerProviders>
  )
}

export type WebPlayerWithMotePlayerProps = WebPlayerProps

export function WebPlayerWithMotePlayer(
  props: PropsWithChildren<WebPlayerWithMotePlayerProps>
) {
  const navigate = useNavigate()
  const auth = useAuth()

  const moteConversationContext = useMoteConversationContext()
  const moteStatus = moteConversationContext.getMoteStatus()
  const playbackLocation = useMotePlaybackLocation()
  const isRecipientView =
    !props.isAnonymous &&
    (!auth.isAuthenticated || !moteStatus.wasCreatedBySameUser)

  const moteDeleteMutation = useMoteDeleteMutation(moteStatus.moteId, true)

  const motePlayerOptions = useMemo(
    () => ({
      init: true,
      src: playbackLocation.url,
      html5: playbackLocation.type === 'remote'
    }),
    [playbackLocation]
  )
  const motePlayer = useMotePlayer(motePlayerOptions)
  const moteEdit = useMoteEdit(
    moteStatus,
    moteStatus.wasCreatedBySameUser,
    moteStatus.moteId,
    playbackLocation.url,
    moteConversationContext.updateMoteStatus,
    undefined,
    props.isAnonymous
  )

  function getMoteTitle() {
    if (moteStatus.displayTitle) {
      return moteStatus.displayTitle
    }

    return isRecipientView ? 'Untitled Mote' : ''
  }

  function getIsTitleSameAsSiteTitle() {
    return (
      moteStatus.displayTitle &&
      moteStatus.displayTitle.toLowerCase() ===
        `on ${moteStatus.containingURLSiteName}`
    )
  }

  function getShouldShowTranscript() {
    return (
      !isRecipientView ||
      (isRecipientView && moteConversationContext.getHasTranscript())
    )
  }

  return (
    <LandingMainLayout
      //@ts-expect-error
      {...motePlayer.getPlayerProps()}
      gridTemplateRows='auto 1fr auto auto'
      headerBackground={!props.isAnonymous ? moteStatus.theme : 'neutral'}
      isAnonymous={props.isAnonymous}
      avatarSrc={moteStatus.createdByAvatar}
      creatorName={moteConversationContext.getCreateByUserName()}
      createdDate={
        'createdAt' in moteStatus
          ? dayjs(moteStatus.createdAt).format('D MMM, h:mma')
          : ''
      }
      title={
        <>
          <ContentEditable
            dataTestId={'mote-title'}
            value={getMoteTitle()}
            onBlur={value => moteEdit.handleEditTitle(value)}
            placeholder={'Untitled mote'}
            isReadonly={isRecipientView}
            newLineDisabled
          />
          {getIsTitleSameAsSiteTitle() && <StickyButton hideTitle />}
        </>
      }
      headerContent={
        <PlayerHeader
          withoutBackground
          title={getMoteTitle()}
          getCanUseMotebook={moteConversationContext.getCanUseBook}
          hasHitMotebookLimit={moteConversationContext.getHitBookLimit}
          saveToMoteBookTextOverwrite={
            props.isAnonymous ? 'Sign up to Save' : undefined
          }
          saveToMoteBookForceTooltip={props.isAnonymous}
          inMotebook={moteStatus.inMotebook}
          onDelete={() => {
            //@ts-expect-error
            moteDeleteMutation
              .mutate({ variables: { isAnonymous: props.isAnonymous } })
              //@ts-expect-error
              .then(mutateValues => {
                if (!mutateValues.error) {
                  navigate('/record', { replace: true })
                }
              })
          }}
          onStar={moteEdit.handleOnSaveToMotebook}
          onTranslate={moteEdit.handleTranscriptTranslation}
          onDownloadClick={moteEdit.handleOnDownload}
          language={moteStatus.translationLanguage}
          buttonVisibility={{
            download: !moteStatus.isLoopy && !isRecipientView,
            delete: !moteStatus.isLoopy && !isRecipientView,
            star: !moteStatus.isLoopy && !isRecipientView,
            translate:
              !moteStatus.isLoopy && moteConversationContext.getHasTranscript()
          }}
        />
      }
      footerContent={
        <>
          <StyledScrubberWrapper>
            <Scrubber
              //@ts-expect-error
              currentTime={motePlayer.position}
              //@ts-expect-error
              duration={motePlayer.duration}
              //@ts-expect-error
              onSeekEnd={motePlayer.onScrubberValueChangeEnd}
              //@ts-expect-error
              onSeek={motePlayer.onScrubberValueChange}
              showTooltip
              animated
            />
            <Durations />
          </StyledScrubberWrapper>
          <StyledPlayerButtonRowWrapper>
            <PlayerButtonRow
              //@ts-expect-error
              onRewind={() => motePlayer.onRewind()}
              //@ts-expect-error
              state={motePlayer.playing ? 'playing' : 'paused'}
              //@ts-expect-error
              onPlay={motePlayer.play}
              //@ts-expect-error
              onPause={motePlayer.pause}
            />
            <ControlsWrapper>
              <PlaybackRateButton
                //@ts-expect-error
                playbackRate={motePlayer.playbackRate}
                //@ts-expect-error
                onClick={motePlayer.onPlaybackRateChange}
              />
              <VolumeRich variant={'web-player'} />
            </ControlsWrapper>
          </StyledPlayerButtonRowWrapper>
        </>
      }
    >
      {moteConversationContext.getIsStickyMote() &&
        !getIsTitleSameAsSiteTitle() && (
          <StyledSticky>
            <StickyButton hideTitle={false} />
          </StyledSticky>
        )}
      <StyledTranscription
        isLoopyMote={moteStatus.isLoopy}
        isStickyButtonPresent={
          moteConversationContext.getIsStickyMote() &&
          !getIsTitleSameAsSiteTitle()
        }
      >
        {getShouldShowTranscript() ? (
          <>
            <Transcription
              dataTestId='mote-transcript'
              placeholder={
                moteConversationContext.getIsWaitingForTranscription()
                  ? 'Transcript is loading...'
                  : 'Start typing to add a transcript'
              }
              size='lg'
              isReadonly={isRecipientView}
              border={isRecipientView ? 'none' : undefined}
              language={moteStatus.transcriptionLanguage}
              value={moteStatus.transcriptDisplay}
              isDisabled={moteConversationContext.getIsWaitingForTranscription()}
              dynamicHeight={false}
              h='full'
              onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) => {
                moteEdit.handleEditTranscript(e.currentTarget.value)
              }}
            />
            {props.children}
          </>
        ) : (
          props.children
        )}
      </StyledTranscription>
    </LandingMainLayout>
  )
}
